import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { IconButton, FormControl, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, useFormikContext } from 'formik';
import { difference, isNull } from 'lodash';
import linkifyIt from 'linkify-it';
import CancelIcon from '@material-ui/icons/Cancel';
import classNames from 'classnames';

import { ContentInput } from 'api';
import { useCurrentUser } from 'hooks';

import UrlPreview from './UrlPreview';
import { ValidatedCheckbox } from 'components';
import BodyTextField from '../Partials/BodyTextField';
import { isContributorAndAbove } from '../../helpers';

const useStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.dark,
    borderRadius: 0,
  },
}));

const linkify = linkifyIt({ fuzzyEmail: false });

const LinkForm: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<ContentInput>();
  const user = useCurrentUser();

  const [removedUrls, setRemovedUrls] = useState([]);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    setCurrentUrl(values.content_object.url);
  }, [values.content_object]);

  function getModelUrl() {
    return values.content_object.url;
  }

  function handleLinkCheck() {
    const value = values.content_object.url;

    if (isNull(value)) {
      return;
    }

    const currentModelUrl = getModelUrl();
    const editorLinkMatches = linkify.match(value) || [];
    const editorUrls = editorLinkMatches.map(match => match.url);
    const availablePreviewUrls = difference(editorUrls, removedUrls);

    if (!currentModelUrl && availablePreviewUrls.length) {
      setFieldValue('content_object.url', availablePreviewUrls[0]);
    }

    setCurrentUrl(availablePreviewUrls[0]);
  }

  const handleRemove = () => {
    const { content_object } = values;

    setFieldValue('content_object.url', '');
    setFieldValue('is_engageable', false);
    setFieldValue('is_shareable', false);

    removedUrls.push(content_object.url);
    setRemovedUrls(removedUrls);
  };

  function onChange(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    setFieldValue(`content_object.url`, event.target.value);
  }

  return (
    <Fragment>
      <BodyTextField />

      <Field name="content_object.url">
        {({ field }: FieldProps<'content_object.url'>) => (
          <TextField
            fullWidth
            variant="outlined"
            value={field.value}
            InputProps={{
              className: classNames({ [classes.link]: !!currentUrl }),
              endAdornment: !!currentUrl && (
                <IconButton onClick={handleRemove}>
                  <CancelIcon />
                </IconButton>
              ),
              name: 'content_object.url',
            }}
            placeholder={t('common:url')}
            onChange={onChange}
            onPaste={handleLinkCheck}
            onKeyPress={event => {
              if (event.key === 'Enter') {
                handleLinkCheck();
              }
            }}
          />
        )}
      </Field>

      <FormControl fullWidth>{currentUrl && <UrlPreview url={currentUrl} />}</FormControl>

      <ValidatedCheckbox name="is_shareable" label={t('posting:shareable')} color="primary" />
      {isContributorAndAbove(user) && (
        <ValidatedCheckbox name="is_branded" label={t('posting:branded')} color="primary" />
      )}
    </Fragment>
  );
};

export default LinkForm;
